import ExcelJS from "exceljs/dist/exceljs.min.js";

const normalizeSheetName = (name = "Sheet1") =>
  name.replace(/[\\/*?:[\]]/g, " ").slice(0, 31) || "Sheet1";

const toArgb = (color) => {
  const values = color?.match(/[\d.]+/g)?.map(Number);
  if (!values || values.length < 3 || values[3] === 0) return null;
  return `FF${values.slice(0, 3).map((value) =>
    Math.round(value).toString(16).padStart(2, "0")
  ).join("")}`.toUpperCase();
};

const getBorder = (style, side) => {
  const cssStyle = style[`border${side}Style`];
  const width = Number.parseFloat(style[`border${side}Width`]);
  if (cssStyle === "none" || width === 0) return undefined;
  const styles = { dashed: "dashed", dotted: "dotted", double: "double" };
  const color = toArgb(style[`border${side}Color`]);
  return {
    style: styles[cssStyle] || (width > 1 ? "medium" : "thin"),
    color: color ? { argb: color } : undefined,
  };
};

const imageToBase64 = async (image) => {
  const response = await fetch(image.currentSrc || image.src);
  const blob = await response.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

const getCellText = (htmlCell) => {
  const text = htmlCell.innerText.trim();
  const controlValues = Array.from(
    htmlCell.querySelectorAll("input, select, textarea")
  )
    .map((control) => control.value?.trim())
    .filter(Boolean);

  return [text, ...controlValues].filter(Boolean).join(" ");
};

const downloadWorkbook = async (workbook, filename) => {
  const buffer = await workbook.xlsx.writeBuffer();
  const url = URL.createObjectURL(new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  }));
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

const TableToExcel = {
  async convert(table, options = {}) {
    if (!table) {
      throw new Error("A table element is required to export an Excel file.");
    }

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(normalizeSheetName(options.sheet?.name));
    const occupied = [];
    const imageTasks = [];

    Array.from(table.rows).forEach((htmlRow, rowIndex) => {
      worksheet.getRow(rowIndex + 1).height = Math.max(
        htmlRow.getBoundingClientRect().height * 0.75,
        15
      );
      let columnIndex = 1;

      Array.from(htmlRow.cells).forEach((htmlCell) => {
        while (occupied[rowIndex]?.[columnIndex]) columnIndex += 1;

        const rowSpan = Number(htmlCell.rowSpan) || 1;
        const colSpan = Number(htmlCell.colSpan) || 1;
        const lastRow = rowIndex + rowSpan;
        const lastColumn = columnIndex + colSpan - 1;
        const cell = worksheet.getCell(rowIndex + 1, columnIndex);
        const style = window.getComputedStyle(htmlCell);
        const backgroundColor = toArgb(style.backgroundColor);
        const fontColor = toArgb(style.color);

        cell.value = getCellText(htmlCell);
        cell.font = {
          name: style.fontFamily.split(",")[0].replace(/["']/g, ""),
          size: Math.max(Number.parseFloat(style.fontSize) * 0.75, 8),
          bold: Number(style.fontWeight) >= 600 || style.fontWeight === "bold",
          italic: style.fontStyle === "italic",
          color: fontColor ? { argb: fontColor } : undefined,
        };
        cell.alignment = {
          horizontal: ["left", "right", "center", "justify"].includes(style.textAlign)
            ? style.textAlign
            : "center",
          vertical: style.verticalAlign === "top"
            ? "top"
            : style.verticalAlign === "bottom" ? "bottom" : "middle",
          wrapText: true,
        };
        cell.border = {
          top: getBorder(style, "Top"),
          right: getBorder(style, "Right"),
          bottom: getBorder(style, "Bottom"),
          left: getBorder(style, "Left"),
        };
        if (backgroundColor) {
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: backgroundColor },
          };
        }

        for (let column = columnIndex; column <= lastColumn; column += 1) {
          worksheet.getColumn(column).width = Math.max(
            worksheet.getColumn(column).width || 0,
            htmlCell.getBoundingClientRect().width / colSpan / 7
          );
        }

        if (rowSpan > 1 || colSpan > 1) {
          worksheet.mergeCells(rowIndex + 1, columnIndex, lastRow, lastColumn);
        }
        for (let row = rowIndex; row < lastRow; row += 1) {
          occupied[row] = occupied[row] || [];
          for (let column = columnIndex; column <= lastColumn; column += 1) {
            occupied[row][column] = true;
          }
        }

        const image = htmlCell.querySelector("img");
        if (image?.src) {
          const imageColumn = columnIndex;
          imageTasks.push(imageToBase64(image).then((base64) => {
            const extension = image.src.split(".").pop()?.split(/[?#]/)[0];
            const imageId = workbook.addImage({
              base64,
              extension: extension === "jpeg" || extension === "jpg" ? "jpeg" : "png",
            });
            worksheet.addImage(imageId, {
              tl: { col: imageColumn - 0.9, row: rowIndex + 0.15 },
              ext: {
                width: Math.min(image.naturalWidth || image.width || 160, 180),
                height: Math.min(image.naturalHeight || image.height || 45, 55),
              },
            });
          }).catch(() => undefined));
        }

        columnIndex = lastColumn + 1;
      });
    });

    await Promise.all(imageTasks);
    await downloadWorkbook(workbook, options.name || "table.xlsx");
  },
};

export default TableToExcel;
